import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const styles = {
  nav: {
    position: "sticky", top: 0, zIndex: 100,
    background: "rgba(11,11,12,0.92)",
    backdropFilter: "blur(12px)",
    borderBottom: "0.5px solid #252528",
    padding: "0 20px",
    display: "flex", alignItems: "center",
    justifyContent: "space-between",
    height: 56,
  },
  left: { display: "flex", alignItems: "center", gap: 24 },
links: { display: "flex", gap: 4 },
  link: {
    fontSize: 13, padding: "5px 14px",
    borderRadius: 20, color: "#52525e",
    cursor: "pointer", transition: "all 0.15s",
    border: "0.5px solid transparent",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontWeight: 500,
    background: "transparent",
  },
  linkActive: {
    color: "#FF5C35",
    border: "0.5px solid rgba(255,92,53,0.3)",
    background: "rgba(255,92,53,0.08)",
  },
  right: { display: "flex", alignItems: "center", gap: 10 },
  searchWrap: {
    background: "#111113",
    border: "0.5px solid #252528",
    borderRadius: 20, padding: "6px 14px",
    display: "flex", alignItems: "center", gap: 8,
  },
  searchInput: {
    background: "transparent", color: "#a0a0a8",
    fontSize: 12, width: 140,
  },
  searchIcon: { color: "#55555e", fontSize: 13 },
  joinBtn: {
    background: "linear-gradient(135deg, #FF5C35, #ff7a58)",
    color: "#fff",
    padding: "7px 18px", borderRadius: 20,
    fontSize: 12, fontWeight: 700,
    fontFamily: "'Bricolage Grotesque', sans-serif",
    letterSpacing: "0.02em",
    transition: "opacity 0.15s",
    boxShadow: "0 2px 12px rgba(255,92,53,0.3)",
    border: "none", cursor: "pointer",
  },
};

const navLinks = [
  { label: "movies",      path: "/",             icon: "🎬" },
  { label: "discussions", path: "/discussions",  icon: "💬" },
  { label: "lists",       path: "/lists",         icon: "📋" },
  { label: "feedback",    path: "/feedback",      icon: "💡" },
];

function UserAvatar({ username, size = 32 }) {
  const letters = username
    ? username.split(/[\s_-]/).map(p => p[0]).slice(0, 2).join("").toUpperCase()
    : "?";
  const hue = username
    ? (username.charCodeAt(0) * 17 + (username.charCodeAt(1) || 0) * 7) % 360
    : 200;
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: `hsl(${hue},40%,16%)`,
      color: `hsl(${hue},65%,62%)`,
      border: `1.5px solid hsl(${hue},40%,26%)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.4, fontWeight: 800, cursor: "pointer", flexShrink: 0,
      fontFamily: "'Bricolage Grotesque', sans-serif",
      userSelect: "none",
    }}>{letters}</div>
  );
}

export default function Navbar() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { currentUser, isLoggedIn, logout } = useAuth();

  const [search,      setSearch]      = useState(() => new URLSearchParams(location.search).get("q") || "");
  const [dropOpen,    setDropOpen]    = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    const q = new URLSearchParams(location.search).get("q") || "";
    setSearch(q);
  }, [location.search]);

  // close dropdown on outside click
  useEffect(() => {
    function handler(e) {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  function handleLogout() {
    setDropOpen(false);
    logout();
    navigate("/");
  }

  const dropItem = (icon, label, onClick) => (
    <div
      onClick={() => { setDropOpen(false); onClick(); }}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "9px 14px", cursor: "pointer",
        fontSize: 13, color: "#c0c0c0",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        transition: "background 0.1s",
        borderRadius: 6,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = "#1a1a1e"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
    >
      <span style={{ fontSize: 15, minWidth: 18 }}>{icon}</span>
      <span>{label}</span>
    </div>
  );

  return (
    <>
    <nav style={styles.nav}>
      <div style={styles.left}>
        <Link to="/" style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          textDecoration: "none",
          lineHeight: 1,
        }}>
          <svg width="28" height="28" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M 24 0 A 18 18 0 1 0 24 36 L 17 36 L 13 45 L 25 36 A 18 18 0 0 0 24 36 Z" fill="#FF5C35"/>
            <path d="M 24 9 A 9 9 0 0 0 15 18 A 9 9 0 0 0 24 27 L 24 21 L 19 21 L 19 18 A 5 5 0 0 1 24 13 Z" fill="#0d0a18"/>
            <path d="M 36 0 A 18 18 0 1 1 36 36 L 43 36 L 47 45 L 35 36 A 18 18 0 0 1 36 36 Z" fill="#ffffff" opacity="0.15"/>
            <path d="M 36 9 A 9 9 0 0 1 45 18 A 9 9 0 0 1 36 27 L 36 21 L 41 21 L 41 18 A 5 5 0 0 0 36 13 Z" fill="#0d0a18"/>
          </svg>
          <span style={{
            fontFamily: "'Bricolage Grotesque', sans-serif",
            fontSize: 20,
            fontWeight: 800,
            letterSpacing: "-0.5px",
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
          }}>
            <span style={{ color: "#FF5C35" }}>Guff</span>
            <span style={{ color: "#f2f2f4" }}>Gaff</span>
          </span>
        </Link>
        <div style={styles.links} className="nav-links">
          {navLinks.map(link => (
            <button
              key={link.path}
              style={{
                ...styles.link,
                ...(location.pathname === link.path ? styles.linkActive : {}),
              }}
              onClick={() => navigate(link.path)}
            >
              {link.label}
            </button>
          ))}
        </div>
      </div>

      <div style={styles.right}>
        {/* Search */}
        <div style={styles.searchWrap} className="nav-search-wrap">
          <span style={styles.searchIcon}>⌕</span>
          <input
            style={styles.searchInput} className="nav-search-input"
            placeholder="search movies..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && search.trim()) {
                navigate(`/search?q=${encodeURIComponent(search.trim())}`);
              }
            }}
          />
          {search && (
            <span
              onClick={() => { setSearch(""); navigate("/"); }}
              style={{ color: "#55555e", cursor: "pointer", fontSize: 14, lineHeight: 1, userSelect: "none" }}
            >×</span>
          )}
        </div>

        {/* Auth area */}
        {isLoggedIn ? (
          <div ref={dropRef} style={{ position: "relative" }}>
            <div onClick={() => setDropOpen(o => !o)}>
              <UserAvatar username={currentUser?.username} />
            </div>

            {dropOpen && (
              <div style={{
                position: "absolute", top: 42, right: 0, minWidth: 180,
                background: "#111113", border: "0.5px solid #252528",
                borderRadius: 12, padding: "6px", zIndex: 200,
                boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
              }}>
                <div style={{
                  padding: "8px 14px 10px",
                  borderBottom: "0.5px solid #1c1c1f",
                  marginBottom: 4,
                }}>
                  <div style={{
                    fontSize: 13, fontWeight: 700, color: "#e0e0e0",
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                  }}>
                    {currentUser?.username}
                  </div>
                  <div style={{ fontSize: 11, color: "#55555e", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    {currentUser?.phone || currentUser?.email || "verified"}
                  </div>
                </div>

                {dropItem("👤", "My Profile",  () => navigate("/profile"))}
                {dropItem("📋", "My Lists",    () => navigate("/lists"))}
                {dropItem("⚙️", "Settings",    () => navigate("/settings"))}

                <div style={{ borderTop: "0.5px solid #1c1c1f", margin: "4px 0" }} />
                {dropItem("🚪", "Sign out", handleLogout)}
              </div>
            )}
          </div>
        ) : (
          <button className="nav-join-btn" style={styles.joinBtn} onClick={() => navigate("/auth")}>
            join the guff
          </button>
        )}
      </div>
    </nav>

    {/* Mobile bottom navigation bar — hidden on desktop via CSS */}
    <div className="mobile-bottom-nav" style={{ display: "none" }}>
      {navLinks.map(link => {
        const active = location.pathname === link.path;
        return (
          <button
            key={link.path}
            onClick={() => navigate(link.path)}
            style={{
              flex: 1, height: "100%",
              background: "transparent", border: "none",
              color: active ? "#FF5C35" : "#52525e",
              fontSize: 9, fontWeight: 600, cursor: "pointer",
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: 3,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              borderTop: active ? "2px solid #FF5C35" : "2px solid transparent",
              transition: "color 0.15s, border-color 0.15s",
            }}
          >
            <span style={{ fontSize: 19, lineHeight: 1 }}>{link.icon}</span>
            {link.label}
          </button>
        );
      })}
    </div>
    </>
  );
}
